// Remembers form options per browser. Saved values are only applied when the
// form still has the field and the value is still valid, so removing or
// renaming an option can't break the form.

type FormValues = Record<string, unknown>;

export const loadSavedFormValues = <T extends FormValues>(
  storageKey: string,
  defaults: T,
  isValid: (field: keyof T, value: unknown) => boolean,
): T => {
  let saved: unknown;
  try {
    saved = JSON.parse(window.localStorage.getItem(storageKey) || "null");
  } catch {
    return defaults;
  }
  if (!saved || typeof saved !== "object") {
    return defaults;
  }

  const values = { ...defaults };
  (Object.keys(defaults) as (keyof T)[]).forEach(field => {
    const value = (saved as FormValues)[field as string];
    if (value !== undefined && isValid(field, value)) {
      values[field] = value as T[keyof T];
    }
  });
  return values;
};

export const saveFormValues = (
  storageKey: string,
  values: FormValues,
  fields: string[],
): void => {
  const saved = Object.fromEntries(fields.map(field => [field, values[field]]));
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(saved));
  } catch {
    // Storage can be unavailable or full, e.g. in private browsing.
  }
};
